/**
 * Can a rewarded ad actually pay out on this device?
 *
 * This is not the same question as "are ads switched on". Shwe Kyat is minted only
 * by a verified AdMob SSV callback (money rule 3), and AdMob sends SSV from the
 * **native** SDK — there is no server-side verification for a rewarded ad on the
 * web. So on a web build `/ads/complete` answers `AD_NOT_VERIFIED` forever, and a
 * player who watches a full ad gets nothing for it.
 *
 * That was live: the CTA was offered to everyone because `ads_enabled` was true.
 *
 * The decision is deliberately UI-only. The server still refuses to mint currency
 * without a real callback, so a tampered client that forces the button back gains
 * nothing but a wasted 30 seconds — which is exactly the property a money rule
 * should have.
 */

import { detectPlatform, readEnv, type Platform } from '@/pwa/install';
import { nativeBridge } from './nativeBridge';

export interface AdFlags {
  ads_enabled: boolean;
  /** Platforms where SSV can reach us — `ads.reward_platforms`, server-owned. */
  ad_reward_platforms: readonly string[];
  /** `ADMOB_WEB_TRUSTED`. Dev only; must never be true in production. */
  ad_reward_web: boolean;
}

/**
 * Which OS's app shell is hosting this page, or null in an ordinary browser.
 *
 * The user agent cannot answer this. The ShweTap app IS an Android System WebView,
 * so it reports the same UA as Chrome on the same phone — and those two differ on
 * precisely the question this module exists to answer. The shell therefore says so
 * itself, by installing `window.ShweTapNative` before the page's first line.
 *
 * This used to sniff for Capacitor, which was the shell chosen in
 * docs/STACK_DECISION.md and then reversed for React Native on 2026-08-05. Nothing
 * ever set that global, so the check answered "browser" inside the real app and hid
 * the Kyat CTA on the one platform that can pay it.
 */
export function shellOs(): 'android' | 'ios' | null {
  const api = nativeBridge();
  return api ? api.platform : null;
}

/** Which platform the *ad SDK* sees, which is not quite the install platform. */
export function adPlatform(env = readEnv()): Platform | 'native' {
  return shellOs() ? 'native' : detectPlatform(env);
}

/**
 * Takes no environment, and that is the point.
 *
 * It used to sniff the user agent, because the shell was assumed to be identifiable that way.
 * It is not: the ShweTap app IS an Android System WebView and reports Chrome's UA. The answer
 * now depends only on two things that can actually be known — whether our own shell installed
 * its bridge, and what the server's flags say — so there is nothing left for a UA to decide.
 */
export function canRewardAds(flags: AdFlags): boolean {
  if (!flags.ads_enabled) return false;

  const os = shellOs();
  /* The shell's own OS, not "either of them". `ad_reward_platforms` is the server's
     kill switch, and the realistic use of it is one store at a time — an iOS build
     held in review, or an Android ad account suspended. Accepting either entry for
     both shells would defeat exactly the case it is for. */
  if (os) return flags.ad_reward_platforms.includes(os);

  /* Browsers — including Safari on an iPhone, which is NOT the iOS app. */
  return flags.ad_reward_web;
}

/**
 * What to tell the player when there is no ad to offer.
 *
 * Never a dead button and never silence: a treasure rock that shows a locked Kyat
 * amount with no way to unlock it reads as a bug, and "come back in the app" is a
 * real answer.
 */
export function noAdReason(flags: AdFlags, env = readEnv()): string {
  if (!flags.ads_enabled) return 'Rewarded ads are paused right now.';
  if (adPlatform(env) === 'native') return 'Rewarded ads are not available yet.';
  return 'Shwe Kyat rewards need the ShweTap app — rewarded ads cannot be verified in a browser.';
}
